import { useEffect, useRef, useState, type RefObject } from "react";
import type { TimeEntry } from "@/api/types";

export const HOUR_PX = 44;
export const SNAP_MIN = 15;
export const DAY_MIN = 24 * 60;
const AXIS_PX = 56; // the w-14 hour gutter
const THRESHOLD_PX = 5; // movement below this is a click, not a drag

// --- pure geometry (unit-tested) --------------------------------------------

export function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** rawMinuteAtY converts a clientY to an unsnapped minute of day. */
export function rawMinuteAtY(clientY: number, gridTop: number): number {
  return clamp(((clientY - gridTop) / HOUR_PX) * 60, 0, DAY_MIN);
}

/** minuteAtY converts a clientY to a minute of day snapped to 15-min steps. */
export function minuteAtY(clientY: number, gridTop: number): number {
  return clamp(Math.round(rawMinuteAtY(clientY, gridTop) / SNAP_MIN) * SNAP_MIN, 0, DAY_MIN);
}

/** slotAtY returns the 15-min slot start under the cursor (click-create anchor). */
export function slotAtY(clientY: number, gridTop: number): number {
  return clamp(Math.floor(rawMinuteAtY(clientY, gridTop) / SNAP_MIN) * SNAP_MIN, 0, DAY_MIN - SNAP_MIN);
}

/** dayIndexAtX maps a clientX to a day-column index within the grid. */
export function dayIndexAtX(clientX: number, gridLeft: number, gridWidth: number, dayCount: number): number {
  const colW = (gridWidth - AXIS_PX) / dayCount;
  return clamp(Math.floor((clientX - gridLeft - AXIS_PX) / colW), 0, dayCount - 1);
}

export function minToHHMM(min: number): string {
  const m = Math.round(min);
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

export interface Laned<T> {
  item: T;
  lane: number;
  lanes: number;
}

/** assignLanes lays overlapping [start,end) intervals out side by side.
 * Greedy interval coloring; `lanes` is the width of the item's overlap cluster. */
export function assignLanes<T>(items: T[], span: (t: T) => [number, number]): Laned<T>[] {
  const sorted = [...items].sort((a, b) => span(a)[0] - span(b)[0] || span(b)[1] - span(a)[1]);
  const laneEnds: number[] = [];
  const out: Laned<T>[] = [];
  let clusterStart = 0;
  let clusterMaxLanes = 0;

  const finishCluster = (endIdx: number) => {
    for (let i = clusterStart; i < endIdx; i++) out[i].lanes = clusterMaxLanes;
  };

  for (const item of sorted) {
    const [s, e] = span(item);
    if (out.length > 0 && laneEnds.every((le) => le <= s)) {
      finishCluster(out.length);
      laneEnds.length = 0;
      clusterStart = out.length;
      clusterMaxLanes = 0;
    }
    let lane = laneEnds.findIndex((le) => le <= s);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(e);
    } else {
      laneEnds[lane] = e;
    }
    clusterMaxLanes = Math.max(clusterMaxLanes, laneEnds.length);
    out.push({ item, lane, lanes: 1 });
  }
  finishCluster(out.length);
  return out;
}

// --- drag state machine ------------------------------------------------------

export interface CalendarGhost {
  dayIdx: number;
  startMin: number;
  endMin: number;
  entry?: TimeEntry;
}

type DragState =
  | { kind: "idle" }
  | { kind: "maybe-create"; dayIdx: number; anchorMin: number; startX: number; startY: number; touch: boolean }
  | { kind: "creating"; dayIdx: number; anchorMin: number; startMin: number; endMin: number }
  | {
      kind: "maybe-block";
      entry: TimeEntry;
      mode: "move" | "resize";
      dayIdx: number;
      startX: number;
      startY: number;
      touch: boolean;
      draggable: boolean;
      startMin: number;
      durMin: number;
      grabOffsetMin: number;
    }
  | { kind: "moving"; entry: TimeEntry; dayIdx: number; startMin: number; durMin: number; grabOffsetMin: number }
  | { kind: "resizing"; entry: TimeEntry; dayIdx: number; startMin: number; endMin: number };

interface UseCalendarDragArgs {
  days: Date[];
  gridRef: RefObject<HTMLDivElement | null>;
  canDrag: (e: TimeEntry) => boolean;
  onClickCreate: (dayIdx: number, slotMin: number) => void;
  onDragCreate: (dayIdx: number, startMin: number, endMin: number) => void;
  onClickEntry: (e: TimeEntry) => void;
  onMove: (e: TimeEntry, dayIdx: number, startMin: number) => void;
  onResize: (e: TimeEntry, endMin: number) => void;
}

/** useCalendarDrag runs the create/move/resize drag machine over the week grid.
 * Clicks are synthesized from pointerup below a 5px movement threshold, so the
 * grid has no separate onClick handlers. Touch gets tap-to-create only — a
 * vertical drag would fight the scroll container. Escape cancels a drag. */
export function useCalendarDrag(args: UseCalendarDragArgs) {
  const [drag, setDrag] = useState<DragState>({ kind: "idle" });
  const dragRef = useRef(drag);
  dragRef.current = drag;
  const argsRef = useRef(args);
  argsRef.current = args;

  const active = drag.kind !== "idle";

  useEffect(() => {
    if (!active) return;
    const grid = () => argsRef.current.gridRef.current?.getBoundingClientRect() ?? null;

    const onPointerMove = (ev: PointerEvent) => {
      const cur = dragRef.current;
      const r = grid();
      if (!r || cur.kind === "idle") return;
      if (cur.kind === "maybe-create" || cur.kind === "maybe-block") {
        const dist = Math.max(Math.abs(ev.clientX - cur.startX), Math.abs(ev.clientY - cur.startY));
        if (dist < THRESHOLD_PX) return;
        if (cur.touch || (cur.kind === "maybe-block" && !cur.draggable)) {
          setDrag({ kind: "idle" }); // let touch scrolling win; locked blocks don't drag
          return;
        }
        if (cur.kind === "maybe-create") {
          const cursorMin = minuteAtY(ev.clientY, r.top);
          setDrag({
            kind: "creating",
            dayIdx: cur.dayIdx,
            anchorMin: cur.anchorMin,
            startMin: Math.min(cur.anchorMin, cursorMin),
            endMin: Math.max(cur.anchorMin + SNAP_MIN, cursorMin),
          });
        } else if (cur.mode === "move") {
          setDrag({ kind: "moving", entry: cur.entry, dayIdx: cur.dayIdx, startMin: cur.startMin, durMin: cur.durMin, grabOffsetMin: cur.grabOffsetMin });
        } else {
          setDrag({
            kind: "resizing",
            entry: cur.entry,
            dayIdx: cur.dayIdx,
            startMin: cur.startMin,
            endMin: clamp(minuteAtY(ev.clientY, r.top), cur.startMin + SNAP_MIN, DAY_MIN),
          });
        }
        return;
      }
      if (cur.kind === "creating") {
        const cursorMin = minuteAtY(ev.clientY, r.top);
        const startMin = Math.min(cur.anchorMin, cursorMin);
        const endMin = Math.max(cur.anchorMin + SNAP_MIN, cursorMin, startMin + SNAP_MIN);
        setDrag({ ...cur, startMin, endMin });
      } else if (cur.kind === "moving") {
        const raw = rawMinuteAtY(ev.clientY, r.top);
        const startMin = clamp(Math.round((raw - cur.grabOffsetMin) / SNAP_MIN) * SNAP_MIN, 0, DAY_MIN - cur.durMin);
        const dayIdx = dayIndexAtX(ev.clientX, r.left, r.width, argsRef.current.days.length);
        setDrag({ ...cur, startMin, dayIdx });
      } else if (cur.kind === "resizing") {
        setDrag({ ...cur, endMin: clamp(minuteAtY(ev.clientY, r.top), cur.startMin + SNAP_MIN, DAY_MIN) });
      }
    };

    const finish = (commit: boolean) => {
      const cur = dragRef.current;
      setDrag({ kind: "idle" });
      if (!commit || cur.kind === "idle") return;
      const a = argsRef.current;
      if (cur.kind === "maybe-create") a.onClickCreate(cur.dayIdx, cur.anchorMin);
      else if (cur.kind === "creating") a.onDragCreate(cur.dayIdx, cur.startMin, cur.endMin);
      else if (cur.kind === "maybe-block") a.onClickEntry(cur.entry);
      else if (cur.kind === "moving") a.onMove(cur.entry, cur.dayIdx, cur.startMin);
      else if (cur.kind === "resizing") a.onResize(cur.entry, cur.endMin);
    };

    const onUp = () => finish(true);
    const onCancel = () => finish(false);
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") finish(false);
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("keydown", onKey);
    };
  }, [active]);

  function columnPointerDown(dayIdx: number, ev: React.PointerEvent) {
    if (ev.button !== 0 || dragRef.current.kind !== "idle") return;
    const r = argsRef.current.gridRef.current?.getBoundingClientRect();
    if (!r) return;
    setDrag({
      kind: "maybe-create",
      dayIdx,
      anchorMin: slotAtY(ev.clientY, r.top),
      startX: ev.clientX,
      startY: ev.clientY,
      touch: ev.pointerType === "touch",
    });
  }

  function blockPointerDown(entry: TimeEntry, dayIdx: number, mode: "move" | "resize", ev: React.PointerEvent) {
    ev.stopPropagation();
    if (ev.button !== 0 || dragRef.current.kind !== "idle") return;
    const r = argsRef.current.gridRef.current?.getBoundingClientRect();
    if (!r) return;
    const s = new Date(entry.start_at);
    const startMin = s.getHours() * 60 + s.getMinutes();
    setDrag({
      kind: "maybe-block",
      entry,
      mode,
      dayIdx,
      startX: ev.clientX,
      startY: ev.clientY,
      touch: ev.pointerType === "touch",
      draggable: argsRef.current.canDrag(entry),
      startMin,
      durMin: Math.max(SNAP_MIN, entry.duration_sec / 60),
      grabOffsetMin: rawMinuteAtY(ev.clientY, r.top) - startMin,
    });
  }

  const ghost: CalendarGhost | null =
    drag.kind === "creating"
      ? { dayIdx: drag.dayIdx, startMin: drag.startMin, endMin: drag.endMin }
      : drag.kind === "moving"
        ? { dayIdx: drag.dayIdx, startMin: drag.startMin, endMin: drag.startMin + drag.durMin, entry: drag.entry }
        : drag.kind === "resizing"
          ? { dayIdx: drag.dayIdx, startMin: drag.startMin, endMin: drag.endMin, entry: drag.entry }
          : null;

  const hiddenEntryId = drag.kind === "moving" || drag.kind === "resizing" ? drag.entry.id : null;
  const dragActive = drag.kind === "creating" || drag.kind === "moving" || drag.kind === "resizing";

  return { ghost, hiddenEntryId, dragActive, columnPointerDown, blockPointerDown };
}
